import { useUser } from '@myosh/myosh-login';
import { type BadgeItemProps, type SideNavigationItem } from '@myosh/odin-components';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { NavigationItem } from '../../../@types/navigation';
import type { AssignedRecordsCount } from '../../../@types/records';
import useUserSchemas from '../../../hooks/use-user-schemas';
import { useAppDispatch } from '../../../redux/hooks';
import { useGetCurrentSchemaInfoQuery, useGetNavigationItemsQuery } from '../../../redux/services/api';
import { useGetAssignedRecordsCountQuery } from '../../../redux/services/record';
import { setNavigationPaths } from '../../../redux/slices/layout';
import { resolveDashboardUrl } from '../../../utils/common-administration-utils';
import BadgeTooltip from '../components/badge-tooltip.component';

const useNavigationItems = (itemClickHandler: (() => () => void | undefined) | undefined) => {
  const { state: userState } = useUser();
  const isAuthenticated = userState.auth;
  const { t } = useTranslation();
  const { currentSchema } = useUserSchemas(userState);
  const dispatch = useAppDispatch();

  const { data: { system_hostname: systemHostname } = {}, isFetching: isFetchingSystemHostname } =
    useGetCurrentSchemaInfoQuery(undefined, { skip: !isAuthenticated });

  const {
    data: navigationItemsData,
    isLoading: isLoadingNavigationItemsData,
    isError,
  } = useGetNavigationItemsQuery(undefined, {
    skip: !isAuthenticated,
  });

  const { data: assignedRecordsCount = [], isFetching: areAssignedRecordCountFetching } =
    useGetAssignedRecordsCountQuery();

  const navigationItems = useMemo(() => {
    if (navigationItemsData && !isLoadingNavigationItemsData && currentSchema?.value && !isFetchingSystemHostname) {
      return navigationItemsData
        .map((navigationItem) => {
          if ('EMBEDDED_URL' === navigationItem.type) {
            return buildEmbeddedNavItem(navigationItem);
          } else if ('MODULE' === navigationItem.type) {
            if ('MODULE' === navigationItem.itemType && navigationItem.viewId) {
              const path = `/${userState.user?.currentSchema}/modules/${navigationItem.viewId}`;
              return {
                label: navigationItem.caption,
                icon: navigationItem.icon,
                to: path,
                badge: {
                  badgeNumber: getAssignedRecordCountForModule(navigationItem.id, assignedRecordsCount),
                  showLoadingIndicator: areAssignedRecordCountFetching,
                  description: React.createElement(BadgeTooltip, {
                    id: navigationItem.id,
                    viewId: navigationItem.viewId,
                    name: navigationItem.caption,
                  }),
                  closeTooltipOnClick: true,
                  lazyLoad: true,
                } as BadgeItemProps,
                onClick: itemClickHandler,
              };
            } else if ('HOMEPAGE' === navigationItem.itemType) {
              return {
                label: t('home'),
                to: '/home',
                icon: 'HOME',
                onClick: itemClickHandler,
              };
            } else if ('DASHBOARD' === navigationItem.itemType) {
              if (userState.isDefaultIdp && !systemHostname) {
                return {
                  label: t('dashboard'),
                  to: '/dashboard',
                  icon: 'BAR_CHART_O',
                  onClick: itemClickHandler,
                };
              } else {
                // explicitly avoid pushing a navigationPaths entry
                return buildEmbeddedNavItem({
                  ...navigationItem,
                  caption: t('dashboard'),
                  icon: 'BAR_CHART_O',
                  sourceUrl: resolveDashboardUrl(systemHostname),
                  target: 'NEW_WINDOW',
                });
              }
            } else if ('VIKING_ANALYTICS' === navigationItem.itemType) {
              return {
                label: t('viking-analytics'),
                to: '/viking-analytics',
                icon: 'BAR_CHART_O',
                onClick: itemClickHandler,
              };
            } else if ('MY_ACTIVITIES' === navigationItem.itemType) {
              const path = `/${currentSchema.value}/my-activities`;
              return {
                label: t('my-activities'),
                to: path,
                icon: 'LIST',
                onClick: itemClickHandler,
              };
            }
            return null;
          } else {
            return null;
          }
        })
        .filter((navigationItem) => navigationItem !== null) as SideNavigationItem[];
    }

    return [];
  }, [
    navigationItemsData,
    isLoadingNavigationItemsData,
    assignedRecordsCount,
    areAssignedRecordCountFetching,
    itemClickHandler,
    currentSchema?.value,
    systemHostname,
    isFetchingSystemHostname,
  ]);

  useEffect(() => {
    if (navigationItems.length > 0) {
      dispatch(
        setNavigationPaths(
          navigationItems
            .filter((item) => typeof item?.to === 'string')
            .map((item) => {
              return { to: item.to as string, target: item.target };
            })
        )
      );
    }
  }, [navigationItems, dispatch]);

  return {
    navigationItems,
    isLoading: isLoadingNavigationItemsData,
    isError,
  };
};

export default useNavigationItems;

// utils
const buildEmbeddedNavItem = (navigationItem: NavigationItem) => {
  if (navigationItem.sourceUrl) {
    if ('NEW_WINDOW' === navigationItem.target) {
      const url = new URL(navigationItem.sourceUrl);
      return {
        label: navigationItem.caption,
        icon: navigationItem.icon,
        to: url.href,
        target: '_blank',
      };
    } else if ('CURRENT_WINDOW' === navigationItem.target) {
      return {
        label: navigationItem.caption,
        icon: navigationItem.icon,
        to: `/embedded/${navigationItem.viewId}`,
        state: {
          embeddedUrl: navigationItem.sourceUrl,
          title: navigationItem.caption,
        },
      };
    }
  }
  return null;
};

const getAssignedRecordCountForModule = (moduleId: number, assginedRecordsCount: AssignedRecordsCount[]) => {
  const currentModule = assginedRecordsCount.find((recordsCount) => recordsCount.moduleId === moduleId);
  return currentModule?.count;
};
